import React, { useState } from "react";
import PropTypes from "prop-types";
import { Link } from "gatsby";
import SEO from "../../components/seo";

const pageHeading = {
	fontFamily: "var(--heading-font)",
	fontWeight: 800,
	fontSize: 32,
	lineHeight: 1.2,
	color: "var(--text)",
	margin: "0 0 20px",
};

const introText = {
	fontFamily: "var(--body-font)",
	fontSize: 17,
	lineHeight: 1.7,
	color: "var(--text-muted)",
	margin: "0 0 40px",
};

const sectionHeading = {
	fontFamily: "var(--heading-font)",
	fontWeight: 700,
	fontSize: 22,
	lineHeight: 1.3,
	color: "var(--text)",
	margin: "0 0 20px",
	borderLeft: "4px solid var(--primary)",
	paddingLeft: 16,
};

const checklistStyle = {
	listStyle: "none",
	margin: "0 0 40px",
	padding: 0,
};

const itemStyle = {
	padding: "12px 0",
	borderBottom: "1px solid var(--border)",
	fontFamily: "var(--body-font)",
	fontSize: 15.5,
};

const labelStyle = {
	display: "flex",
	alignItems: "center",
	gap: 12,
	cursor: "pointer",
	width: "100%",
};

const checkboxStyle = {
	width: 20,
	height: 20,
	accentColor: "var(--primary)",
	flexShrink: 0,
	cursor: "pointer",
};

const noteBox = {
	background: "var(--tint-warm)",
	border: "1px solid var(--warm-light)",
	borderRadius: 12,
	padding: "20px 24px",
	marginBottom: 0,
};

const noteHeading = {
	fontFamily: "var(--heading-font)",
	fontWeight: 700,
	fontSize: 15.5,
	color: "var(--warm-dark)",
	margin: "0 0 8px",
};

const noteText = {
	fontFamily: "var(--body-font)",
	fontSize: 15,
	lineHeight: 1.7,
	color: "var(--text)",
	margin: 0,
};

const ChecklistItem = ({ name, done, text, toggleChecked }) => {
	const isDone = done.includes(name);
	const toggle = () => toggleChecked(name);
	return (
		<li
			style={{
				...itemStyle,
				color: isDone ? "var(--text-muted)" : "var(--text)",
				textDecoration: isDone ? "line-through" : "none",
			}}
		>
			<label style={labelStyle} htmlFor={name}>
				<input id={name} style={checkboxStyle} type="checkbox" checked={isDone} onChange={toggle} />
				<span>{text}</span>
			</label>
		</li>
	);
};

ChecklistItem.propTypes = {
	name: PropTypes.string.isRequired,
	done: PropTypes.arrayOf(PropTypes.string).isRequired,
	text: PropTypes.string.isRequired,
	toggleChecked: PropTypes.func.isRequired,
};

const FirstVisit = () => {
	const [done, setDone] = useState([]);
	const toggleChecked = name => {
		if (done.includes(name)) {
			setDone(done.filter(el => el !== name));
		} else {
			setDone([name, ...done]);
		}
	};
	return (
		<div>
			<SEO
				title="First Visit"
				keywords={[
					`First visit`,
					`Information`,
					`Things to bring`,
					`Checklist`,
					`Gastroenterologist`,
					`Hepatologist`,
					`Dr Frank`,
					`Dr Brennan`,
					`Dr Spiro`,
					`Dr Raftopoulos`,
					`Dr Ian Yusoff`,
					`Dr Dev Segarajasingam`,
					`Dr Sam Rao`,
					`Dr Duy Pham`,
					`Dr Claire Gordon`,
				]}
				description="Checklist to help you with your first visit"
			/>

			<h1 style={pageHeading}>First Visit</h1>
			<p style={introText}>
				As a busy specialist practice, appointments are scheduled once we&apos;ve received and triaged a
				referral from your GP — see our{" "}
				<Link to="/patient-information/new-referrals" style={{ color: "var(--primary)" }}>
					New Referrals
				</Link>{" "}
				page for how your GP can send this through. Once your appointment is confirmed, here&apos;s what to
				bring on the day.
			</p>

			<h2 style={sectionHeading}>What to bring</h2>
			<ul style={checklistStyle}>
				<ChecklistItem name="1" done={done} text="A list of your current medications" toggleChecked={toggleChecked} />
				<ChecklistItem name="2" done={done} text="Results of any recent blood tests" toggleChecked={toggleChecked} />
				<ChecklistItem
					name="3"
					done={done}
					text="Any relevant X-rays or previous reports"
					toggleChecked={toggleChecked}
				/>
				<ChecklistItem name="4" done={done} text="Your Medicare card" toggleChecked={toggleChecked} />
				<ChecklistItem name="5" done={done} text="Your private health fund details" toggleChecked={toggleChecked} />
			</ul>

			<div style={noteBox}>
				<p style={noteHeading}>Don&apos;t forget your doctor&apos;s referral</p>
				<p style={noteText}>
					A valid referral from your GP or another specialist is required for your appointment and to claim a
					Medicare rebate, so please remember to bring it with you.
				</p>
			</div>
		</div>
	);
};

export default FirstVisit;
